import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLanguage } from '../contexts/LanguageContext'
import { useCart } from '../contexts/CartContext'
import MediaWithShimmer from '../components/MediaWithShimmer'

const Cart = () => {
  const { language } = useLanguage()
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart()
  const navigate = useNavigate()

  const formatPrice = (price) => {
    const formatted = new Intl.NumberFormat('ru-RU').format(price).replace(/\s/g, '\u00A0')
    return `${formatted}\u00A0€`
  }

  const getProductName = (product) => {
    return language === 'en' ? product.nameEn : language === 'es' ? product.nameEs : product.name
  }

  const translations = {
    title: { ru: 'Корзина', en: 'Shopping Cart', es: 'Carrito' },
    empty: { ru: 'Ваша корзина пуста', en: 'Your cart is empty', es: 'Tu carrito está vacío' },
    continueShopping: { ru: 'Продолжить покупки', en: 'Continue Shopping', es: 'Continuar Comprando' },
    product: { ru: 'Товар', en: 'Product', es: 'Producto' },
    size: { ru: 'Размер', en: 'Size', es: 'Talla' },
    color: { ru: 'Цвет', en: 'Color', es: 'Color' },
    price: { ru: 'Цена', en: 'Price', es: 'Precio' },
    quantity: { ru: 'Количество', en: 'Quantity', es: 'Cantidad' },
    total: { ru: 'Итого', en: 'Total', es: 'Total' },
    remove: { ru: 'Удалить', en: 'Remove', es: 'Eliminar' },
    subtotal: { ru: 'Промежуточный итог', en: 'Subtotal', es: 'Subtotal' },
    shipping: { ru: 'Доставка', en: 'Shipping', es: 'Envío' },
    shippingFree: { ru: 'Бесплатно', en: 'Free', es: 'Gratis' },
    orderTotal: { ru: 'Итого к оплате', en: 'Order Total', es: 'Total del Pedido' },
    checkout: { ru: 'Оформить заказ', en: 'Proceed to Checkout', es: 'Proceder al Pago' }
  }

  const t = translations

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="ri-shopping-bag-line text-8xl text-gray-300 mb-6"></i>
          <h1 className="text-3xl font-light mb-6 playfair">{t.empty[language]}</h1>
          <Link
            to="/shop"
            className="inline-block bg-black text-white px-8 py-3 hover:bg-gray-800 transition-colors montserrat"
          >
            {t.continueShopping[language]}
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 pb-6">
        <h1 className="text-4xl font-light mb-8 playfair">{t.title[language]}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, index) => {
              const itemImages = Array.isArray(item.images) && item.images.length > 0 
                ? item.images 
                : (item.image ? [item.image] : [])
              
              return (
              <div key={`${item.id}-${item.size}-${item.color}-${index}`} className="bg-white p-6 flex flex-col sm:flex-row gap-6">
                <div className="w-32 h-32 flex-shrink-0">
                  <MediaWithShimmer
                    src={itemImages[0] || ''}
                    alt={getProductName(item)}
                    className="w-full h-full object-contain"
                  />
                </div>
                
                <div className="flex-1 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <Link
                        to={`/shop/${item.id}`}
                        className="text-lg font-light hover:underline playfair"
                      >
                        {getProductName(item)}
                      </Link>
                      <div className="mt-2 space-y-1 text-sm text-gray-600 montserrat">
                        {item.size && (
                          <p>
                            {t.size[language]}: <span className="text-black">{item.size}</span>
                          </p>
                        )}
                        {item.color && (
                          <p>
                            {t.color[language]}: <span className="text-black">{item.color}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id, item.size, item.color)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <i className="ri-delete-bin-line text-xl"></i>
                    </button>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity - 1)}
                        className="w-8 h-8 border border-gray-300 hover:border-black transition-colors"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="text-lg montserrat w-8 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.size, item.color, item.quantity + 1)}
                        className="w-8 h-8 border border-gray-300 hover:border-black transition-colors"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <p className="text-xl font-medium montserrat">
                      {formatPrice(item.price * item.quantity)}
                    </p>
                  </div>
                </div>
              </div>
            )})}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 sticky top-24">
              <h2 className="text-2xl font-light mb-6 playfair">
                {language === 'ru' ? 'Итого' : language === 'en' ? 'Order Summary' : 'Resumen del Pedido'}
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between montserrat">
                  <span className="text-gray-600">{t.subtotal[language]}</span>
                  <span className="font-medium">{formatPrice(getCartTotal())}</span>
                </div>
                <div className="flex justify-between montserrat">
                  <span className="text-gray-600">{t.shipping[language]}</span>
                  <span className="font-medium">{t.shippingFree[language]}</span>
                </div>
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex justify-between text-lg montserrat">
                    <span>{t.orderTotal[language]}</span>
                    <span className="font-medium">{formatPrice(getCartTotal())}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full bg-black text-white py-4 hover:bg-gray-800 transition-colors mb-3 montserrat"
              >
                {t.checkout[language]}
              </button>

              <Link
                to="/shop"
                className="block text-center text-gray-600 hover:text-black transition-colors montserrat text-sm"
              >
                {t.continueShopping[language]}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Cart

